import { useMemo } from 'react';
import type { ColumnDef, OnChangeFn, RowSelectionState, SortingState } from '@tanstack/react-table';
import { DataTable } from '@/shared/ui/DataTable';
import type { Appointment } from '@/features/appointments';
import { AppointmentStatusPill } from './AppointmentStatusPill';

interface Column<T> {
  key: string;
  header: string;
  render: (item: T) => React.ReactNode;
  className?: string;
}

interface AppointmentTableProps {
  data: Appointment[];
  columns: Column<Appointment>[];
  isLoading?: boolean;
  isError?: boolean;
  errorMessage?: string;
  emptyMessage?: string;
  onRowClick?: (id: string) => void;
  selectedIds?: Set<string>;
  onSelectionChange?: (ids: Set<string>) => void;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onSort?: (key: string) => void;
  renderStatusCell?: (row: Appointment) => React.ReactNode;
  footer?: React.ReactNode;
}

/**
 * Appointments grid. A thin adapter over the shared {@link DataTable} that keeps
 * its column/selection/sort API (server-driven) so callers stay unchanged.
 */
export function AppointmentTable({
  data,
  columns,
  isLoading,
  isError,
  emptyMessage,
  onRowClick,
  selectedIds,
  onSelectionChange,
  sortBy,
  sortOrder,
  onSort,
  renderStatusCell,
  footer,
}: AppointmentTableProps) {
  const tableColumns = useMemo<ColumnDef<Appointment>[]>(
    () =>
      columns.map((col) => ({
        id: col.key,
        header: col.header,
        // Dummy accessor so headers are sortable; sorting itself is server-side.
        accessorFn: () => null,
        enableSorting: Boolean(onSort) && col.key !== 'actions',
        meta: {
          label: col.header || (col.key === 'actions' ? 'Actions' : col.key),
          headerClassName: col.className,
          cellClassName: col.className,
        },
        cell: ({ row }) =>
          col.key === 'status' && renderStatusCell ? (
            renderStatusCell(row.original)
          ) : col.key === 'status' ? (
            <AppointmentStatusPill status={row.original.status} />
          ) : (
            col.render(row.original)
          ),
      })),
    [columns, onSort, renderStatusCell],
  );

  const rowSelection = useMemo<RowSelectionState>(() => {
    if (!selectedIds) return {};
    return Object.fromEntries([...selectedIds].map((id) => [id, true]));
  }, [selectedIds]);

  const handleSelectionChange: OnChangeFn<RowSelectionState> = (updater) => {
    if (!onSelectionChange) return;
    const next = typeof updater === 'function' ? updater(rowSelection) : updater;
    onSelectionChange(new Set(Object.keys(next).filter((id) => next[id])));
  };

  const sorting = useMemo<SortingState>(
    () => (sortBy ? [{ id: sortBy, desc: sortOrder === 'desc' }] : []),
    [sortBy, sortOrder],
  );

  const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
    if (!onSort) return;
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    // `onSort` toggles direction for the same key and resets to asc for a new key.
    onSort(first ? first.id : (sortBy ?? ''));
  };

  return (
    <div className="overflow-hidden rounded-xl2 border border-[var(--border-subtle)] bg-[var(--bg-surface)] shadow-card">
      <DataTable<Appointment, unknown>
        columns={tableColumns}
        data={data}
        getRowId={(appointment) => appointment.id}
        onRowClick={onRowClick ? (appointment) => onRowClick(appointment.id) : undefined}
        isLoading={isLoading}
        isError={isError}
        enableSorting={Boolean(onSort)}
        manualSorting
        sorting={sorting}
        onSortingChange={handleSortingChange}
        enableRowSelection={Boolean(onSelectionChange)}
        rowSelection={rowSelection}
        onRowSelectionChange={handleSelectionChange}
        aria-label="Appointments"
        emptyState={
          <div className="p-12 text-center text-sm text-[var(--text-secondary)]">
            {emptyMessage ?? 'No appointments found'}
          </div>
        }
        footer={footer}
      />
    </div>
  );
}
